import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Alert } from '@/components/ui/alert'
import { approvalsApi } from '@/lib/api/approvals'
import type { PaymentListItem } from '@/lib/api/payments'

interface RejectDialogProps {
  payment: PaymentListItem | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const formatAmount = (paise: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(paise / 100)

export default function RejectDialog({ payment, open, onClose, onSuccess }: RejectDialogProps) {
  const [reason, setReason] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const rejectMutation = useMutation({
    mutationFn: () => approvalsApi.reject(payment!.id, reason),
    onSuccess: () => {
      onSuccess()
      handleClose()
    },
    onError: (err: any) => {
      setErrorMsg(err?.error ?? 'Failed to reject payment. Please try again.')
    },
  })

  function handleClose() {
    setReason('')
    setErrorMsg('')
    onClose()
  }

  if (!payment) return null

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Payment</DialogTitle>
          <DialogDescription>
            {formatAmount(payment.amount)} to <span className="font-medium">{payment.payeeName}</span> via {payment.paymentMode}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {errorMsg && (
            <Alert variant="destructive">
              <p className="text-sm">{errorMsg}</p>
            </Alert>
          )}
          <div className="space-y-1">
            <Label htmlFor="reject-reason">Reason for rejection</Label>
            <Input
              id="reject-reason"
              placeholder="e.g. Incorrect account number, duplicate payment…"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 255))}
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">{reason.length}/255</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={rejectMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => rejectMutation.mutate()}
            disabled={reason.trim().length === 0 || rejectMutation.isPending}
          >
            {rejectMutation.isPending ? 'Rejecting…' : 'Reject Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
